// /src/pages/ResetPassword.js
// This page is linked from the email: /reset-password?token=<uuid>

import { useState, useEffect } from "react";
import { Loader } from "lucide-react";

export default function ResetPassword() {
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) setToken(t);
    else setMessage("No reset token found. Please request a new reset link.");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (newPassword !== confirmPassword) {
      setMessage("Passwords don't match.");
      return;
    }
    if (newPassword.length < 8) {
      setMessage("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/resetpassword", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      const data = await res.json();

      if (data.success) {
        setSuccess(true);
        setMessage("✅ Password reset! Redirecting to login...");
        setTimeout(() => { window.location.href = "/login"; }, 2500);
      } else {
        setMessage(data.error || "Reset failed. The link may have expired.");
      }
    } catch (err) {
      setMessage("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoArea}>
          <div style={styles.logoMark}>P</div>
          <div style={styles.logoText}>Propertunity</div>
        </div>

        <h1 style={styles.heading}>Set a new password</h1>
        <p style={styles.subheading}>Enter and confirm your new password below.</p>

        {!success && token && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>New password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirm new password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="Repeat your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <Loader size={16} style={{ animation: "spin 0.8s linear infinite" }} />
                  Resetting...
                </span>
              ) : "Reset password"}
            </button>
          </form>
        )}

        {message && (
          <div style={success ? styles.successBanner : styles.errorBanner}>
            {message}
          </div>
        )}

        <div style={{ marginTop: 20, textAlign: "center" }}>
          <a href="/login" style={{ fontSize: 13, color: "#60a5fa", textDecoration: "none" }}>
            Back to login
          </a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#0a0f1e", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'DM Sans', sans-serif" },
  card: { background: "#111827", border: "1px solid #1f2937", borderRadius: 16, padding: "48px 40px", width: "100%", maxWidth: 420, boxShadow: "0 24px 80px rgba(0,0,0,0.5)" },
  logoArea: { display: "flex", alignItems: "center", gap: 10, marginBottom: 32 },
  logoMark: { width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", color: "#fff", fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" },
  logoText: { fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 18, color: "#f9fafb", letterSpacing: "-0.3px" },
  heading: { fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, color: "#f9fafb", margin: "0 0 6px", letterSpacing: "-0.5px" },
  subheading: { fontSize: 14, color: "#6b7280", margin: "0 0 32px" },
  form: { display: "flex", flexDirection: "column", gap: 20 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: "#9ca3af" },
  input: { background: "#1f2937", border: "1px solid #374151", borderRadius: 8, padding: "12px 14px", fontSize: 14, color: "#f9fafb", width: "100%", boxSizing: "border-box" },
  submitBtn: { background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", color: "#fff", border: "none", borderRadius: 8, padding: "13px", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 4 },
  errorBanner: { marginTop: 16, background: "#1f0a0a", border: "1px solid #7f1d1d", color: "#f87171", borderRadius: 8, padding: "10px 14px", fontSize: 13 },
  successBanner: { marginTop: 16, background: "#0a1f0a", border: "1px solid #166534", color: "#4ade80", borderRadius: 8, padding: "10px 14px", fontSize: 13 },
};